import { Box, Stack, AlertIcon, Alert } from '@chakra-ui/react'
import React, { useState, useEffect, memo, useCallback } from 'react'
import {
  MICROPHONE_PERMISSION_STATUS,
  MICROPHONE_PERMISSION_STATUS_MESSAGES,
  useCallContact,
  useCallDurationLabel,
  useMicrophonePermissions,
  useTwilioCallConnection,
} from '../contact-center.hooks'
import { DialerType, KeyPad } from './KeyPad'
import { useDialerCallStatus, useDialerContacts, useDialerState, useDialerTab, useTwilioCall } from './dialer.hooks'
import { DialerHeader } from './DialerHeader'
import { ContactList } from './ContactList'
import { CallingScreen } from './CallingScreen'
import { DialerStateDefaultValue, DialerStateType, DialerTab } from '../contact-center.store'
import { DialerFooter } from './DialerFooter'
import { CallLog } from '../call-log/CallLog'

export interface DialerProps {}

export const Dialer: React.FC<DialerProps> = memo((props): JSX.Element => {
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined)
  const contacts = useDialerContacts(searchQuery)
  const { tab } = useDialerTab()
  const { dialerState, setDialerState } = useDialerState()
  const { status: callStatus } = useDialerCallStatus()
  const { setLabel: setDurationLabel } = useCallDurationLabel()
  const [microphoneErrorCode, setMicrophoneErrorCode] = useState<MICROPHONE_PERMISSION_STATUS>(
    MICROPHONE_PERMISSION_STATUS.NONE,
  )
  const { setCallFunc } = useCallContact()

  const onDisconnect = useCallback(() => {
    setDialerState(prev => ({ ...prev, isCalling: false }))
    setDurationLabel('')
    setDialerState(DialerStateDefaultValue)
  }, [setDialerState])

  const { connectCall, muteCall, endCurrentCall } = useTwilioCall(onDisconnect)
  const { connection: callConnection } = useTwilioCallConnection();
  
  const { launchPrompt, queryPermissions } = useMicrophonePermissions({ setErrorMessage: setMicrophoneErrorCode })

  

  const microphoneErrorAlertStatus = React.useMemo<any>(() => {
    switch (microphoneErrorCode) {
      case MICROPHONE_PERMISSION_STATUS.BLOCKED:
        return 'error'
      case MICROPHONE_PERMISSION_STATUS.DENIED:
        return 'error'
      case MICROPHONE_PERMISSION_STATUS.GRANTED:
        return 'success'
      case MICROPHONE_PERMISSION_STATUS.PROMPT:
        return 'info'
    }
  }, [microphoneErrorCode])

  useEffect(() => {
    if (!callStatus.isKeyPadOpen || !dialerState.isCalling) return
    if (!callConnection) return
    if (!dialerState.keyPressed?.length) return

    callConnection.sendDigits('w')
    callConnection.sendDigits(dialerState.keyPressed)
    console.log('Sending Digit', dialerState.keyPressed)
  }, [callStatus.isKeyPadOpen, callConnection, dialerState.keyPressed, dialerState.isCalling])

  const handleInlineCall = async (dialerState: DialerStateType | string) => {
    setTimeout(() => queryPermissions(), 200)

    await launchPrompt()
    const permission = await queryPermissions()

    if (permission.state === 'granted') {
      if (typeof dialerState === 'string') connectCall(dialerState)
      else connectCall(dialerState?.number)
    }
  }

  useEffect(() => {
    if (setCallFunc) setCallFunc(prev => ({ ...prev, call: handleInlineCall }))
  }, [setCallFunc])

  useEffect(() => {
    return () => {
      callConnection?.disconnect()
      dialerState.TwilioDevice?.disconnectAll()
      //dialerState.TwilioDevice?.destroy()
    }
  }, [])
  return (
    <Box minH="888px" p={0} m={0} display="flex" flexDirection="column">
      <DialerHeader searchQuery={searchQuery} setSearchQuery={setSearchQuery} />

      {DialerTab.CONTACT === tab && (
        <Box data-testid-="contact-center-contact-list">
          <ContactList contacts={contacts} call={handleInlineCall} />
        </Box>
      )}

      {DialerTab.LOG === tab && (
        <Box data-testid-="contact-center-call-logs">
          <CallLog />
        </Box>
      )}

      <Box pos="relative" data-testid="contact-center-num-pad">
        {dialerState.isOpen && !dialerState.isCalling && (
          <KeyPad type={DialerType.BeforeCall} connectCall={handleInlineCall} />
        )}
      </Box>

      {dialerState.isCalling && (
        <Box mt="10px">
          <Stack spacing="25px" dir="column">
            {microphoneErrorCode !== MICROPHONE_PERMISSION_STATUS.NONE && (
              <Alert status={microphoneErrorAlertStatus} top="-14px" height="60px">
                <AlertIcon />
                {MICROPHONE_PERMISSION_STATUS_MESSAGES[microphoneErrorCode]}
              </Alert>
            )}
          </Stack>
          <CallingScreen endCurrentCall={endCurrentCall} muteCurrentCall={muteCall} />
          {callStatus.isKeyPadOpen && (
            <Box>
              <KeyPad type={DialerType.AfterCall} />
            </Box>
          )}
        </Box>
      )}

      <Box marginTop="auto">
        <DialerFooter />
      </Box>
    </Box>
  )
})
